import React, { useEffect, useMemo } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Card, 
  CardContent, 
  Button
} from '@mui/material';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../i18n/LanguageContext';
import './VideoTutorials.css';

function VideoTutorials() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Scroll to top on load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Safe retrieval of translated videos
  const videosData = t('videoTutorials.videos');
  const videos = useMemo(() => {
    return Array.isArray(videosData) ? videosData : [];
  }, [videosData]);

  return (
    <Container maxWidth="lg" className="video-tutorials-container animate-fade-in">
      {/* Torna alla Homepage Button */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Button 
          variant="text" 
          startIcon={<ArrowBackIcon />} 
          onClick={() => navigate('/homepage')}
          sx={{ 
            color: '#1d659c', 
            fontWeight: 600,
            '&:hover': {
              backgroundColor: 'rgba(29, 101, 156, 0.08)'
            }
          }}
        >
          {t('videoTutorials.backToHome') || 'Torna alla Homepage'}
        </Button>
      </Box>

      {/* Hero Header */}
      <Box className="tutorials-hero">
        <Typography 
          variant="h3" 
          component="h1" 
          gutterBottom 
          sx={{ 
            fontWeight: 800, 
            fontSize: { xs: '2rem', md: '3rem' },
            fontFamily: 'Ubuntu-Black, sans-serif'
          }}
        >
          {t('videoTutorials.title') || 'Video Tutorial'}
        </Typography>
        <Typography 
          variant="h6" 
          sx={{ 
            maxWidth: '800px', 
            margin: '0 auto', 
            opacity: 0.9,
            fontSize: { xs: '1rem', md: '1.25rem' } 
          }}
        >
          {t('videoTutorials.subtitle') || 'Scopri come utilizzare la piattaforma con le nostre video guide.'}
        </Typography>
      </Box>

      {/* Lista dei Video */}
      {videos.length > 0 ? (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          gap: 4, 
          mt: 4,
          width: '100%'
        }}>
          {videos.map((video) => (
            <Card key={video.youtubeId} className="video-card" elevation={1} sx={{ width: '100%', maxWidth: '850px' }}>
              {/* Iframe player di YouTube */}
              <Box className="video-iframe-wrapper">
                <iframe
                  src={`https://www.youtube.com/embed/${video.youtubeId}`}
                  title={video.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                ></iframe>
              </Box>

              {/* Contenuto della Scheda (Solo Titolo) */}
              <CardContent sx={{ p: 2, display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: '64px' }}>
                <Typography 
                  variant="h6" 
                  component="h2" 
                  sx={{ 
                    fontWeight: 700, 
                    color: '#1a1a1a',
                    lineHeight: 1.3,
                    textAlign: 'center'
                  }}
                >
                  {video.title}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      ) : (
        <Box sx={{ py: 8, textAlign: 'center' }}>
          <PlayCircleIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            {t('videoTutorials.noVideos') || 'Nessun video tutorial trovato.'}
          </Typography>
        </Box>
      )}
    </Container>
  );
}

export default VideoTutorials;
